import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft,
  ArrowRight,
  User,
  Heart,
  Pill,
  Dumbbell,
  Target,
  Apple,
  Scale,
  Brain,
  MessageSquare,
  FileSignature,
  Check,
  Loader2,
  Activity,
  Bike,
  AlertTriangle
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Basic Info', icon: User, description: 'Personal details & contact' },
  { id: 2, title: 'Medical History', icon: Heart, description: 'Health & PAR-Q' },
  { id: 3, title: 'Medications', icon: Pill, description: 'Medications & lifestyle' },
  { id: 4, title: 'Fitness Level', icon: Dumbbell, description: 'Experience & equipment' },
  { id: 5, title: 'Goals', icon: Target, description: 'Objectives & timeline' },
  { id: 6, title: 'Nutrition', icon: Apple, description: 'Diet & preferences' },
  { id: 7, title: 'Body Composition', icon: Scale, description: 'Measurements & baseline' },
  { id: 8, title: 'Mindset', icon: Brain, description: 'Mental readiness' },
  { id: 9, title: 'Communication', icon: MessageSquare, description: 'Contact preferences' },
  { id: 10, title: 'Agreement', icon: FileSignature, description: 'Legal & consent' }
];

const INITIAL_FORM = {
  basic_info: {
    full_name: '',
    date_of_birth: '',
    age: null,
    gender_identity: '',
    height_feet: null,
    height_inches: null,
    weight_lbs: null,
    email: '',
    phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    location: ''
  },
  medical_health: {
    parq_heart_condition: false,
    parq_chest_pain_activity: false,
    parq_chest_pain_rest: false,
    parq_balance_dizziness: false,
    parq_bone_joint_problem: false,
    parq_blood_pressure_medication: false,
    parq_other_reason: false,
    current_injuries: [],
    chronic_pain_areas: [],
    past_surgeries: [],
    recent_surgeries: [],
    surgery_knee: false,
    surgery_hip: false,
    surgery_shoulder: false,
    surgery_back: false,
    surgery_heart: false,
    surgery_abdominal: false,
    surgery_other: '',
    cardiovascular_conditions: [],
    diabetes_metabolic: false,
    diabetes_type: '',
    respiratory_issues: [],
    pregnancy_status: 'none',
    physician_clearance_required: false,
    physician_clearance_obtained: false,
    physician_name: '',
    physician_phone: '',
    medically_cleared_confirmation: false
  },
  medications_lifestyle: {
    current_medications: [],
    hormone_therapy: false,
    hormone_therapy_type: '',
    sleep_quality: 5,
    stress_level: 5,
    occupation: '',
    occupation_activity: 'sedentary',
    average_daily_steps: null
  },
  fitness_experience: {
    training_experience: 'beginner',
    years_training: null,
    current_workout_frequency: null,
    gym_access: 'home',
    equipment_dumbbells: false,
    equipment_barbell: false,
    equipment_bands: false,
    equipment_kettlebells: false,
    equipment_machines: false,
    equipment_none: false,
    equipment_other: [],
    preferred_training_style: ''
  },
  goals_motivation: {
    primary_goal: 'general_health',
    timeline: '90_days',
    why_statement: ''
  },
  nutrition_lifestyle: {
    dietary_preference: 'omnivore',
    food_allergies: [],
    daily_meals: 3,
    alcohol_intake: 'occasional',
    soda_intake: 'none',
    water_intake: '4_to_8_cups',
    sugar_intake: 'moderate',
    previous_diet_history: '',
    macro_tracking_experience: false,
    food_logging_habits: 'none'
  },
  body_composition_baseline: {
    body_fat_percentage: null,
    waist_inches: null,
    hip_inches: null,
    chest_inches: null,
    resting_heart_rate: null,
    vo2_max: null,
    bench_press_1rm: null,
    squat_1rm: null,
    deadlift_1rm: null
  },
  mental_emotional: {
    confidence_level: 5,
    biggest_obstacle: '',
    preferred_coaching_tone: 'encouraging'
  },
  communication_preferences: {
    preferred_contact: 'in_app',
    coaching_style_preference: 'encouraging'
  },
  legal_agreements: {
    liability_waiver_accepted: false,
    assumption_of_risk_accepted: false,
    payment_policy_acknowledged: false,
    photo_video_consent: false,
    data_privacy_policy_accepted: false,
    electronic_agreement_confirmed: false,
    signature: '',
    signature_date: ''
  },
  movement_screening: {
    squat_comfort: 5,
    shoulder_mobility: 5,
    back_pain_during_hinging: false
  },
  energy_profile: {
    morning_or_night_trainer: 'flexible',
    training_stress_tolerance: 5
  },
  cycling_performance: {
    ftp_estimate: null,
    weekly_mileage: null,
    bike_discipline: ''
  }
};

export default function ClientIntakeFormPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [existingForm, setExistingForm] = useState(null);

  const isTrainerEditing = clientId && (user?.role === 'admin' || user?.role === 'trainer');
  const targetUserId = clientId || user?.id;

  useEffect(() => {
    fetchExistingForm();
  }, [targetUserId, token]);

  useEffect(() => {
    // Pre-fill email from user if not editing another client
    if (!clientId && user?.email && !formData.basic_info.email) {
      setFormData(prev => ({
        ...prev,
        basic_info: { ...prev.basic_info, email: user.email }
      }));
    }
  }, [user, clientId]);

  const fetchExistingForm = async () => {
    setLoading(true);
    try {
      const endpoint = isTrainerEditing 
        ? `${API}/clients/intake/${clientId}`
        : `${API}/clients/intake`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.form || response.data.basic_info) {
        const form = response.data.form || response.data;
        setExistingForm(form);
        // Merge existing data with defaults
        setFormData(prev => ({
          ...prev,
          ...form,
          basic_info: { ...prev.basic_info, ...form.basic_info },
          medical_health: { ...prev.medical_health, ...form.medical_health },
          medications_lifestyle: { ...prev.medications_lifestyle, ...form.medications_lifestyle },
          fitness_experience: { ...prev.fitness_experience, ...form.fitness_experience },
          goals_motivation: { ...prev.goals_motivation, ...form.goals_motivation },
          nutrition_lifestyle: { ...prev.nutrition_lifestyle, ...form.nutrition_lifestyle },
          body_composition_baseline: { ...prev.body_composition_baseline, ...form.body_composition_baseline },
          mental_emotional: { ...prev.mental_emotional, ...form.mental_emotional },
          communication_preferences: { ...prev.communication_preferences, ...form.communication_preferences },
          legal_agreements: { ...prev.legal_agreements, ...form.legal_agreements }
        }));
      }
    } catch (error) {
      // 404 is expected if no form exists yet
      if (error.response?.status !== 404) {
        console.error('Error fetching form:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSection = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleArrayField = (section, field, value, add = true) => {
    setFormData(prev => {
      const current = prev[section][field] || [];
      const updated = add 
        ? [...current, value]
        : current.filter(v => v !== value);
      return {
        ...prev,
        [section]: { ...prev[section], [field]: updated }
      };
    });
  };

  const handleSubmit = async () => {
    // Validate required fields in legal section
    if (!formData.legal_agreements.electronic_agreement_confirmed) {
      toast.error('You must confirm the electronic agreement');
      return;
    }
    if (!formData.legal_agreements.liability_waiver_accepted) {
      toast.error('You must accept the liability waiver');
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = isTrainerEditing 
        ? `${API}/clients/intake/${clientId}`
        : `${API}/clients/intake`;
      
      await axios.post(endpoint, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Intake form submitted successfully!');
      
      if (isTrainerEditing) {
        navigate(`/clients/${clientId}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (currentStep / STEPS.length) * 100;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <User className="w-6 h-6 text-primary" /> Basic Information
            </h2>
            <p className="text-gray-400">Let's start with your personal details.</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-gray-300 text-sm block mb-1">Full Name *</label>
                <Input
                  value={formData.basic_info.full_name}
                  onChange={(e) => updateSection('basic_info', 'full_name', e.target.value)}
                  className="bg-secondary border-border"
                  placeholder="John Doe"
                  data-testid="intake-full-name"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Date of Birth *</label>
                <Input
                  type="date"
                  value={formData.basic_info.date_of_birth}
                  onChange={(e) => updateSection('basic_info', 'date_of_birth', e.target.value)}
                  className="bg-secondary border-border"
                  data-testid="intake-dob"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Gender Identity (Optional)</label>
                <select
                  value={formData.basic_info.gender_identity}
                  onChange={(e) => updateSection('basic_info', 'gender_identity', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                >
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non_binary">Non-binary</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Height</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Feet"
                    value={formData.basic_info.height_feet || ''}
                    onChange={(e) => updateSection('basic_info', 'height_feet', parseInt(e.target.value) || null)}
                    className="bg-secondary border-border"
                  />
                  <Input
                    type="number"
                    placeholder="Inches"
                    value={formData.basic_info.height_inches || ''}
                    onChange={(e) => updateSection('basic_info', 'height_inches', parseInt(e.target.value) || null)}
                    className="bg-secondary border-border"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Weight (lbs)</label>
                <Input
                  type="number"
                  value={formData.basic_info.weight_lbs || ''}
                  onChange={(e) => updateSection('basic_info', 'weight_lbs', parseFloat(e.target.value) || null)}
                  className="bg-secondary border-border"
                  placeholder="175"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Email *</label>
                <Input
                  type="email"
                  value={formData.basic_info.email}
                  onChange={(e) => updateSection('basic_info', 'email', e.target.value)}
                  className="bg-secondary border-border"
                  data-testid="intake-email"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Phone *</label>
                <Input
                  type="tel"
                  value={formData.basic_info.phone}
                  onChange={(e) => updateSection('basic_info', 'phone', e.target.value)}
                  className="bg-secondary border-border"
                  placeholder="(555) 123-4567"
                  data-testid="intake-phone"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Time Zone</label>
                <Input
                  value={formData.basic_info.timezone}
                  onChange={(e) => updateSection('basic_info', 'timezone', e.target.value)}
                  className="bg-secondary border-border"
                  placeholder="America/New_York"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Location</label>
                <Input
                  value={formData.basic_info.location}
                  onChange={(e) => updateSection('basic_info', 'location', e.target.value)}
                  className="bg-secondary border-border"
                  placeholder="City, State"
                />
              </div>
            </div>
            
            <div className="border-t border-border pt-4 mt-6">
              <h3 className="text-lg text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Emergency Contact
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-gray-300 text-sm block mb-1">Name *</label>
                  <Input
                    value={formData.basic_info.emergency_contact_name}
                    onChange={(e) => updateSection('basic_info', 'emergency_contact_name', e.target.value)}
                    className="bg-secondary border-border"
                    data-testid="intake-emergency-name"
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm block mb-1">Phone *</label>
                  <Input
                    type="tel"
                    value={formData.basic_info.emergency_contact_phone}
                    onChange={(e) => updateSection('basic_info', 'emergency_contact_phone', e.target.value)}
                    className="bg-secondary border-border"
                    data-testid="intake-emergency-phone"
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm block mb-1">Relationship</label>
                  <Input
                    value={formData.basic_info.emergency_contact_relationship}
                    onChange={(e) => updateSection('basic_info', 'emergency_contact_relationship', e.target.value)}
                    className="bg-secondary border-border"
                    placeholder="Spouse, Parent, Friend"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <Heart className="w-6 h-6 text-primary" /> Medical & Health History
            </h2>
            <p className="text-gray-400">PAR-Q+ Physical Activity Readiness Questionnaire</p>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 mb-4">
              <p className="text-red-400 text-sm">
                Please answer YES or NO to the following questions. If you answer YES to any question, 
                consult your physician before starting a physical activity program.
              </p>
            </div>
            
            <div className="space-y-4">
              {[
                { key: 'parq_heart_condition', label: 'Has your doctor ever said that you have a heart condition and should only do physical activity recommended by a doctor?' },
                { key: 'parq_chest_pain_activity', label: 'Do you feel pain in your chest when you do physical activity?' },
                { key: 'parq_chest_pain_rest', label: 'In the past month, have you had chest pain when you were not doing physical activity?' },
                { key: 'parq_balance_dizziness', label: 'Do you lose your balance because of dizziness or do you ever lose consciousness?' },
                { key: 'parq_bone_joint_problem', label: 'Do you have a bone or joint problem that could be made worse by a change in your physical activity?' },
                { key: 'parq_blood_pressure_medication', label: 'Is your doctor currently prescribing medication for your blood pressure or heart condition?' },
                { key: 'parq_other_reason', label: 'Do you know of any other reason why you should not do physical activity?' }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-start gap-3 p-3 bg-secondary/30 rounded-md">
                  <Checkbox
                    checked={formData.medical_health[key]}
                    onCheckedChange={(checked) => updateSection('medical_health', key, checked)}
                    className="mt-1"
                  />
                  <label className="text-white text-sm cursor-pointer">
                    {label}
                  </label>
                </div>
              ))}
            </div>
            
            <div className="border-t border-border pt-4 mt-6">
              <h3 className="text-lg text-white mb-4">Current Injuries or Chronic Pain</h3>
              <Textarea
                value={formData.medical_health.current_injuries?.join(', ') || ''}
                onChange={(e) => updateSection('medical_health', 'current_injuries', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                className="bg-secondary border-border"
                placeholder="List any current injuries (separated by commas)"
                rows={2}
              />
            </div>
            
            <div>
              <h3 className="text-lg text-white mb-4">Surgery History (Check all that apply)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: 'surgery_knee', label: 'Knee' },
                  { key: 'surgery_hip', label: 'Hip' },
                  { key: 'surgery_shoulder', label: 'Shoulder' },
                  { key: 'surgery_back', label: 'Back/Spine' },
                  { key: 'surgery_heart', label: 'Heart' },
                  { key: 'surgery_abdominal', label: 'Abdominal' }
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.medical_health[key]}
                      onCheckedChange={(checked) => updateSection('medical_health', key, checked)}
                    />
                    <label className="text-gray-300">{label}</label>
                  </div>
                ))}
              </div>
              <Input
                value={formData.medical_health.surgery_other || ''}
                onChange={(e) => updateSection('medical_health', 'surgery_other', e.target.value)}
                className="bg-secondary border-border mt-3"
                placeholder="Other surgeries..."
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 text-sm block mb-1">Pregnancy Status</label>
                <select
                  value={formData.medical_health.pregnancy_status}
                  onChange={(e) => updateSection('medical_health', 'pregnancy_status', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                >
                  <option value="none">Not Applicable</option>
                  <option value="pregnant">Currently Pregnant</option>
                  <option value="postpartum">Postpartum (within 6 months)</option>
                </select>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-md">
                <Checkbox
                  checked={formData.medical_health.physician_clearance_required}
                  onCheckedChange={(checked) => updateSection('medical_health', 'physician_clearance_required', checked)}
                />
                <label className="text-white text-sm">Physician clearance required?</label>
              </div>
            </div>
            
            <div className="bg-green-500/10 border border-green-500/30 rounded-md p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={formData.medical_health.medically_cleared_confirmation}
                  onCheckedChange={(checked) => updateSection('medical_health', 'medically_cleared_confirmation', checked)}
                  className="mt-1"
                />
                <label className="text-green-400 text-sm">
                  <strong>I confirm I am medically cleared for physical activity.</strong> I have consulted 
                  with my physician if necessary and understand that I participate at my own risk.
                </label>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <Pill className="w-6 h-6 text-primary" /> Medications & Lifestyle
            </h2>
            
            <div>
              <label className="text-gray-300 text-sm block mb-1">Current Medications</label>
              <Textarea
                value={formData.medications_lifestyle.current_medications?.join(', ') || ''}
                onChange={(e) => updateSection('medications_lifestyle', 'current_medications', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                className="bg-secondary border-border"
                placeholder="List medications (separated by commas)"
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-md">
              <Checkbox
                checked={formData.medications_lifestyle.hormone_therapy}
                onCheckedChange={(checked) => updateSection('medications_lifestyle', 'hormone_therapy', checked)}
              />
              <label className="text-white">Currently on hormone therapy (TRT/HRT)?</label>
            </div>
            
            {formData.medications_lifestyle.hormone_therapy && (
              <div>
                <label className="text-gray-300 text-sm block mb-1">Type of hormone therapy</label>
                <Input
                  value={formData.medications_lifestyle.hormone_therapy_type || ''}
                  onChange={(e) => updateSection('medications_lifestyle', 'hormone_therapy_type', e.target.value)}
                  className="bg-secondary border-border"
                  placeholder="TRT, HRT, etc."
                />
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 text-sm block mb-2">Sleep Quality (1-10)</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.medications_lifestyle.sleep_quality}
                    onChange={(e) => updateSection('medications_lifestyle', 'sleep_quality', parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-white font-mono w-8 text-center">{formData.medications_lifestyle.sleep_quality}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Poor</span>
                  <span>Excellent</span>
                </div>
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-2">Stress Level (1-10)</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.medications_lifestyle.stress_level}
                    onChange={(e) => updateSection('medications_lifestyle', 'stress_level', parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-white font-mono w-8 text-center">{formData.medications_lifestyle.stress_level}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Low</span>
                  <span>High</span>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 text-sm block mb-1">Occupation</label>
                <Input
                  value={formData.medications_lifestyle.occupation || ''}
                  onChange={(e) => updateSection('medications_lifestyle', 'occupation', e.target.value)}
                  className="bg-secondary border-border"
                  placeholder="Software Engineer, Nurse, etc."
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Occupation Activity Level</label>
                <select
                  value={formData.medications_lifestyle.occupation_activity}
                  onChange={(e) => updateSection('medications_lifestyle', 'occupation_activity', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                >
                  <option value="sedentary">Sedentary (desk job)</option>
                  <option value="light">Light (some standing/walking)</option>
                  <option value="moderate">Moderate (regularly active)</option>
                  <option value="active">Active (physical job)</option>
                  <option value="very_active">Very Active (labor intensive)</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-1">Average Daily Steps</label>
              <Input
                type="number"
                value={formData.medications_lifestyle.average_daily_steps || ''}
                onChange={(e) => updateSection('medications_lifestyle', 'average_daily_steps', parseInt(e.target.value) || null)}
                className="bg-secondary border-border"
                placeholder="5000"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <Dumbbell className="w-6 h-6 text-primary" /> Fitness Experience
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 text-sm block mb-1">Training Experience</label>
                <select
                  value={formData.fitness_experience.training_experience}
                  onChange={(e) => updateSection('fitness_experience', 'training_experience', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                  data-testid="intake-experience"
                >
                  <option value="beginner">Beginner (0-1 years)</option>
                  <option value="intermediate">Intermediate (1-3 years)</option>
                  <option value="advanced">Advanced (3+ years)</option>
                </select>
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Years Training</label>
                <Input
                  type="number"
                  value={formData.fitness_experience.years_training || ''}
                  onChange={(e) => updateSection('fitness_experience', 'years_training', parseInt(e.target.value) || null)}
                  className="bg-secondary border-border"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Current Workout Frequency (days/week)</label>
                <Input
                  type="number"
                  min="0"
                  max="7"
                  value={formData.fitness_experience.current_workout_frequency || ''}
                  onChange={(e) => updateSection('fitness_experience', 'current_workout_frequency', parseInt(e.target.value) || null)}
                  className="bg-secondary border-border"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Gym Access</label>
                <select
                  value={formData.fitness_experience.gym_access}
                  onChange={(e) => updateSection('fitness_experience', 'gym_access', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                >
                  <option value="home">Home Only</option>
                  <option value="commercial_gym">Commercial Gym</option>
                  <option value="travel">Travel/Hotel Gyms</option>
                  <option value="hybrid">Hybrid (Home + Gym)</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-3">Equipment Available (Check all)</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: 'equipment_dumbbells', label: 'Dumbbells' },
                  { key: 'equipment_barbell', label: 'Barbell' },
                  { key: 'equipment_bands', label: 'Resistance Bands' },
                  { key: 'equipment_kettlebells', label: 'Kettlebells' },
                  { key: 'equipment_machines', label: 'Machines' },
                  { key: 'equipment_none', label: 'None' }
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2 p-2 bg-secondary/30 rounded">
                    <Checkbox
                      checked={formData.fitness_experience[key]}
                      onCheckedChange={(checked) => updateSection('fitness_experience', key, checked)}
                    />
                    <label className="text-gray-300">{label}</label>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-1">Preferred Training Style</label>
              <select
                value={formData.fitness_experience.preferred_training_style}
                onChange={(e) => updateSection('fitness_experience', 'preferred_training_style', e.target.value)}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
              >
                <option value="">Select...</option>
                <option value="strength">Strength</option>
                <option value="fat_loss">Fat Loss</option>
                <option value="athletic">Athletic Performance</option>
                <option value="cycling_performance">Cycling Performance</option>
              </select>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <Target className="w-6 h-6 text-primary" /> Goals & Motivation
            </h2>
            <p className="text-gray-400">What drives you? This helps us personalize your journey.</p>
            
            <div>
              <label className="text-gray-300 text-sm block mb-2">Primary Goal *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { value: 'fat_loss', label: 'Fat Loss', icon: '🔥' },
                  { value: 'muscle_gain', label: 'Muscle Gain', icon: '💪' },
                  { value: 'strength', label: 'Strength', icon: '🏋️' },
                  { value: 'endurance', label: 'Endurance', icon: '🏃' },
                  { value: 'rehab', label: 'Rehabilitation', icon: '🩺' },
                  { value: 'general_health', label: 'General Health', icon: '❤️' }
                ].map(({ value, label, icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateSection('goals_motivation', 'primary_goal', value)}
                    className={`p-4 rounded-md border text-left transition-all ${
                      formData.goals_motivation.primary_goal === value
                        ? 'border-primary bg-primary/20 text-white'
                        : 'border-border bg-secondary/30 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-2xl mb-1">{icon}</div>
                    <div className="font-medium">{label}</div>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-1">Timeline</label>
              <select
                value={formData.goals_motivation.timeline}
                onChange={(e) => updateSection('goals_motivation', 'timeline', e.target.value)}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
              >
                <option value="30_days">30 Days</option>
                <option value="90_days">90 Days</option>
                <option value="6_months">6 Months</option>
                <option value="12_months">12 Months</option>
                <option value="18_months">18 Months</option>
                <option value="24_months">24 Months</option>
                <option value="30_months">30 Months</option>
                <option value="36_months">36 Months</option>
              </select>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-1">Your WHY Statement</label>
              <p className="text-gray-500 text-xs mb-2">This powers your daily motivational messages.</p>
              <Textarea
                value={formData.goals_motivation.why_statement || ''}
                onChange={(e) => updateSection('goals_motivation', 'why_statement', e.target.value)}
                className="bg-secondary border-border"
                placeholder="Why is this goal important to you? What will achieving it mean for your life?"
                rows={4}
                data-testid="intake-why"
              />
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <Apple className="w-6 h-6 text-primary" /> Nutrition & Lifestyle
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 text-sm block mb-1">Dietary Preference</label>
                <select
                  value={formData.nutrition_lifestyle.dietary_preference}
                  onChange={(e) => updateSection('nutrition_lifestyle', 'dietary_preference', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                >
                  <option value="omnivore">Omnivore</option>
                  <option value="vegetarian">Vegetarian</option>
                  <option value="vegan">Vegan</option>
                  <option value="keto">Keto</option>
                  <option value="pescatarian">Pescatarian</option>
                </select>
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Daily Meals</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.nutrition_lifestyle.daily_meals || ''}
                  onChange={(e) => updateSection('nutrition_lifestyle', 'daily_meals', parseInt(e.target.value) || null)}
                  className="bg-secondary border-border"
                />
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-1">Food Allergies</label>
              <Textarea
                value={formData.nutrition_lifestyle.food_allergies?.join(', ') || ''}
                onChange={(e) => updateSection('nutrition_lifestyle', 'food_allergies', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                className="bg-secondary border-border"
                placeholder="Peanuts, Shellfish, Dairy, etc."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-gray-300 text-sm block mb-1">Alcohol</label>
                <select
                  value={formData.nutrition_lifestyle.alcohol_intake}
                  onChange={(e) => updateSection('nutrition_lifestyle', 'alcohol_intake', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white text-sm"
                >
                  <option value="none">None</option>
                  <option value="occasional">Occasional</option>
                  <option value="moderate">Moderate</option>
                  <option value="frequent">Frequent</option>
                </select>
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Soda</label>
                <select
                  value={formData.nutrition_lifestyle.soda_intake}
                  onChange={(e) => updateSection('nutrition_lifestyle', 'soda_intake', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white text-sm"
                >
                  <option value="none">None</option>
                  <option value="occasional">Occasional</option>
                  <option value="daily">Daily</option>
                  <option value="multiple_daily">Multiple/Day</option>
                </select>
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Water</label>
                <select
                  value={formData.nutrition_lifestyle.water_intake}
                  onChange={(e) => updateSection('nutrition_lifestyle', 'water_intake', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white text-sm"
                >
                  <option value="less_than_4_cups">&lt;4 cups</option>
                  <option value="4_to_8_cups">4-8 cups</option>
                  <option value="8_plus_cups">8+ cups</option>
                </select>
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Sugar</label>
                <select
                  value={formData.nutrition_lifestyle.sugar_intake}
                  onChange={(e) => updateSection('nutrition_lifestyle', 'sugar_intake', e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white text-sm"
                >
                  <option value="low">Low</option>
                  <option value="moderate">Moderate</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-1">Previous Diet History</label>
              <Textarea
                value={formData.nutrition_lifestyle.previous_diet_history || ''}
                onChange={(e) => updateSection('nutrition_lifestyle', 'previous_diet_history', e.target.value)}
                className="bg-secondary border-border"
                placeholder="Any diets you've tried before..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-md">
              <Checkbox
                checked={formData.nutrition_lifestyle.macro_tracking_experience}
                onCheckedChange={(checked) => updateSection('nutrition_lifestyle', 'macro_tracking_experience', checked)}
              />
              <label className="text-white">I have experience tracking macros</label>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <Scale className="w-6 h-6 text-primary" /> Body Composition & Performance
            </h2>
            <p className="text-gray-400">Optional baseline measurements to track your transformation.</p>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-gray-300 text-sm block mb-1">Body Fat % (if known)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.body_composition_baseline?.body_fat_percentage || ''}
                  onChange={(e) => updateSection('body_composition_baseline', 'body_fat_percentage', parseFloat(e.target.value) || null)}
                  className="bg-secondary border-border"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Waist (inches)</label>
                <Input
                  type="number"
                  step="0.25"
                  value={formData.body_composition_baseline?.waist_inches || ''}
                  onChange={(e) => updateSection('body_composition_baseline', 'waist_inches', parseFloat(e.target.value) || null)}
                  className="bg-secondary border-border"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Hips (inches)</label>
                <Input
                  type="number"
                  step="0.25"
                  value={formData.body_composition_baseline?.hip_inches || ''}
                  onChange={(e) => updateSection('body_composition_baseline', 'hip_inches', parseFloat(e.target.value) || null)}
                  className="bg-secondary border-border"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Chest (inches)</label>
                <Input
                  type="number"
                  step="0.25"
                  value={formData.body_composition_baseline?.chest_inches || ''}
                  onChange={(e) => updateSection('body_composition_baseline', 'chest_inches', parseFloat(e.target.value) || null)}
                  className="bg-secondary border-border"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">Resting Heart Rate</label>
                <Input
                  type="number"
                  value={formData.body_composition_baseline?.resting_heart_rate || ''}
                  onChange={(e) => updateSection('body_composition_baseline', 'resting_heart_rate', parseInt(e.target.value) || null)}
                  className="bg-secondary border-border"
                  placeholder="BPM"
                />
              </div>
              
              <div>
                <label className="text-gray-300 text-sm block mb-1">VO2 Max (optional)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.body_composition_baseline?.vo2_max || ''}
                  onChange={(e) => updateSection('body_composition_baseline', 'vo2_max', parseFloat(e.target.value) || null)}
                  className="bg-secondary border-border"
                />
              </div>
            </div>
            
            <div className="border-t border-border pt-4">
              <h3 className="text-lg text-white mb-4">1RM Estimates (Optional)</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-gray-300 text-sm block mb-1">Bench Press (lbs)</label>
                  <Input
                    type="number"
                    value={formData.body_composition_baseline?.bench_press_1rm || ''}
                    onChange={(e) => updateSection('body_composition_baseline', 'bench_press_1rm', parseFloat(e.target.value) || null)}
                    className="bg-secondary border-border"
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm block mb-1">Squat (lbs)</label>
                  <Input
                    type="number"
                    value={formData.body_composition_baseline?.squat_1rm || ''}
                    onChange={(e) => updateSection('body_composition_baseline', 'squat_1rm', parseFloat(e.target.value) || null)}
                    className="bg-secondary border-border"
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm block mb-1">Deadlift (lbs)</label>
                  <Input
                    type="number"
                    value={formData.body_composition_baseline?.deadlift_1rm || ''}
                    onChange={(e) => updateSection('body_composition_baseline', 'deadlift_1rm', parseFloat(e.target.value) || null)}
                    className="bg-secondary border-border"
                  />
                </div>
              </div>
            </div>
            
            {/* Movement Screening */}
            <div className="border-t border-border pt-4">
              <h3 className="text-lg text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" /> Movement Screening
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 text-sm block mb-2">Squat Comfort (1-10)</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={formData.movement_screening?.squat_comfort || 5}
                      onChange={(e) => updateSection('movement_screening', 'squat_comfort', parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-white font-mono w-8">{formData.movement_screening?.squat_comfort || 5}</span>
                  </div>
                </div>
                <div>
                  <label className="text-gray-300 text-sm block mb-2">Shoulder Mobility (1-10)</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={formData.movement_screening?.shoulder_mobility || 5}
                      onChange={(e) => updateSection('movement_screening', 'shoulder_mobility', parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-white font-mono w-8">{formData.movement_screening?.shoulder_mobility || 5}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-md mt-3">
                <Checkbox
                  checked={formData.movement_screening?.back_pain_during_hinging || false}
                  onCheckedChange={(checked) => updateSection('movement_screening', 'back_pain_during_hinging', checked)}
                />
                <label className="text-white">I experience back pain during hinge movements (deadlifts, RDLs)</label>
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <Brain className="w-6 h-6 text-primary" /> Mental & Emotional Readiness
            </h2>
            <p className="text-gray-400">Understanding your mindset helps us tailor your coaching experience.</p>
            
            <div>
              <label className="text-gray-300 text-sm block mb-2">Confidence Level (1-10)</label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={formData.mental_emotional?.confidence_level || 5}
                  onChange={(e) => updateSection('mental_emotional', 'confidence_level', parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-white font-mono text-xl w-12 text-center">{formData.mental_emotional?.confidence_level || 5}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Not Confident</span>
                <span>Very Confident</span>
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-2">Biggest Obstacle</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { value: 'time', label: 'Time' },
                  { value: 'motivation', label: 'Motivation' },
                  { value: 'injury', label: 'Injury' },
                  { value: 'nutrition', label: 'Nutrition' },
                  { value: 'consistency', label: 'Consistency' }
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateSection('mental_emotional', 'biggest_obstacle', value)}
                    className={`p-3 rounded-md border text-center transition-all ${
                      formData.mental_emotional?.biggest_obstacle === value
                        ? 'border-primary bg-primary/20 text-white'
                        : 'border-border bg-secondary/30 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-2">Preferred Coaching Tone</label>
              <p className="text-gray-500 text-xs mb-3">This helps AI personalize your daily messages.</p>
              <div className="grid md:grid-cols-3 gap-3">
                {[
                  { value: 'educational', label: 'Educational', desc: 'Teach me the "why"' },
                  { value: 'motivational', label: 'Motivational', desc: 'Keep me inspired' },
                  { value: 'tough_love', label: 'Tough Love', desc: 'Hold me accountable' }
                ].map(({ value, label, desc }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateSection('mental_emotional', 'preferred_coaching_tone', value)}
                    className={`p-4 rounded-md border text-left transition-all ${
                      formData.mental_emotional?.preferred_coaching_tone === value
                        ? 'border-primary bg-primary/20 text-white'
                        : 'border-border bg-secondary/30 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <div className="font-medium mb-1">{label}</div>
                    <div className="text-xs">{desc}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Energy Profile */}
            <div className="border-t border-border pt-4">
              <h3 className="text-lg text-white mb-4">Energy Profile</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 text-sm block mb-1">Best Training Time</label>
                  <select
                    value={formData.energy_profile?.morning_or_night_trainer || 'flexible'}
                    onChange={(e) => updateSection('energy_profile', 'morning_or_night_trainer', e.target.value)}
                    className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                  >
                    <option value="morning">Morning Person</option>
                    <option value="evening">Evening Trainer</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-300 text-sm block mb-2">Training Stress Tolerance (1-10)</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={formData.energy_profile?.training_stress_tolerance || 5}
                      onChange={(e) => updateSection('energy_profile', 'training_stress_tolerance', parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-white font-mono w-8">{formData.energy_profile?.training_stress_tolerance || 5}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Cycling (conditional) */}
            {formData.fitness_experience.preferred_training_style === 'cycling_performance' && (
              <div className="border-t border-border pt-4">
                <h3 className="text-lg text-white mb-4 flex items-center gap-2">
                  <Bike className="w-5 h-5 text-primary" /> Cycling Performance
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-gray-300 text-sm block mb-1">FTP Estimate (watts)</label>
                    <Input
                      type="number"
                      value={formData.cycling_performance?.ftp_estimate || ''}
                      onChange={(e) => updateSection('cycling_performance', 'ftp_estimate', parseInt(e.target.value) || null)}
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div>
                    <label className="text-gray-300 text-sm block mb-1">Weekly Mileage</label>
                    <Input
                      type="number"
                      value={formData.cycling_performance?.weekly_mileage || ''}
                      onChange={(e) => updateSection('cycling_performance', 'weekly_mileage', parseInt(e.target.value) || null)}
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div>
                    <label className="text-gray-300 text-sm block mb-1">Bike Discipline</label>
                    <select
                      value={formData.cycling_performance?.bike_discipline || ''}
                      onChange={(e) => updateSection('cycling_performance', 'bike_discipline', e.target.value)}
                      className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="road">Road</option>
                      <option value="mountain">Mountain</option>
                      <option value="gravel">Gravel</option>
                      <option value="indoor">Indoor/Trainer</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-primary" /> Communication Preferences
            </h2>
            
            <div>
              <label className="text-gray-300 text-sm block mb-2">Preferred Contact Method</label>
              <div className="grid md:grid-cols-3 gap-3">
                {[
                  { value: 'in_app', label: 'In-App', desc: 'Notifications & messages' },
                  { value: 'email', label: 'Email', desc: 'Email updates' },
                  { value: 'sms', label: 'SMS', desc: 'Text messages' }
                ].map(({ value, label, desc }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateSection('communication_preferences', 'preferred_contact', value)}
                    className={`p-4 rounded-md border text-left transition-all ${
                      formData.communication_preferences.preferred_contact === value
                        ? 'border-primary bg-primary/20 text-white'
                        : 'border-border bg-secondary/30 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <div className="font-medium mb-1">{label}</div>
                    <div className="text-xs">{desc}</div>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-2">Coaching Style Preference</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'encouraging', label: 'Encouraging', desc: 'Supportive and positive' },
                  { value: 'direct', label: 'Direct', desc: 'Straight to the point' },
                  { value: 'educational', label: 'Educational', desc: 'Explain the reasoning' },
                  { value: 'high_accountability', label: 'High Accountability', desc: 'Push me to excel' }
                ].map(({ value, label, desc }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateSection('communication_preferences', 'coaching_style_preference', value)}
                    className={`p-4 rounded-md border text-left transition-all ${
                      formData.communication_preferences.coaching_style_preference === value
                        ? 'border-primary bg-primary/20 text-white'
                        : 'border-border bg-secondary/30 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <div className="font-medium mb-1">{label}</div>
                    <div className="text-xs">{desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 10:
        return (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-primary" /> Legal Agreements
            </h2>
            <p className="text-gray-400">Please review and accept the following agreements to complete your intake.</p>
            
            <div className="space-y-4">
              {[
                { key: 'liability_waiver_accepted', label: 'Liability Waiver', desc: 'I acknowledge the inherent risks of physical exercise and release Bodies By Akeem from liability for any injuries.' },
                { key: 'assumption_of_risk_accepted', label: 'Assumption of Risk', desc: 'I voluntarily assume all risks associated with participating in fitness activities.' },
                { key: 'payment_policy_acknowledged', label: 'Payment Policy', desc: 'I understand and agree to the payment terms and cancellation policy.' },
                { key: 'photo_video_consent', label: 'Photo/Video Consent (Optional)', desc: 'I consent to the use of my progress photos/videos for marketing purposes.', optional: true },
                { key: 'data_privacy_policy_accepted', label: 'Data & Privacy Policy', desc: 'I have read and agree to the data privacy policy and how my information will be used.' }
              ].map(({ key, label, desc, optional }) => (
                <div key={key} className={`p-4 rounded-md border ${
                  formData.legal_agreements[key] ? 'border-green-500/50 bg-green-500/10' : 'border-border bg-secondary/30'
                }`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={formData.legal_agreements[key]}
                      onCheckedChange={(checked) => updateSection('legal_agreements', key, checked)}
                      className="mt-1"
                    />
                    <div>
                      <div className="text-white font-medium flex items-center gap-2">
                        {label}
                        {optional && <span className="text-xs text-gray-500">(Optional)</span>}
                      </div>
                      <p className="text-gray-400 text-sm mt-1">{desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-primary/10 border border-primary/30 rounded-md p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={formData.legal_agreements.electronic_agreement_confirmed}
                  onCheckedChange={(checked) => {
                    updateSection('legal_agreements', 'electronic_agreement_confirmed', checked);
                    if (checked) {
                      updateSection('legal_agreements', 'signature_date', new Date().toISOString().split('T')[0]);
                    }
                  }}
                  className="mt-1"
                  data-testid="intake-final-agreement"
                />
                <div>
                  <div className="text-primary font-bold">Electronic Agreement *</div>
                  <p className="text-gray-300 text-sm mt-1">
                    I acknowledge that submitting this form constitutes my electronic agreement to all terms 
                    and conditions listed above. This serves as my digital signature.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 text-sm block mb-1">Digital Signature (Type your full name)</label>
              <Input
                value={formData.legal_agreements.signature || ''}
                onChange={(e) => updateSection('legal_agreements', 'signature', e.target.value)}
                className="bg-secondary border-border font-cursive text-lg"
                placeholder="Type your full name"
                data-testid="intake-signature"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="client-intake-form-page" className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="p-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase">
            CLIENT <span className="text-primary">INTAKE FORM</span>
          </h1>
          <p className="text-gray-400 text-sm">
            {isTrainerEditing ? 'Complete intake for your client' : 'Complete your fitness profile'}
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="card-tactical">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-400 text-sm">Step {currentStep} of {STEPS.length}</span>
          <span className="text-primary font-mono">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
        
        {/* Step Indicators */}
        <div className="flex justify-between mt-4 overflow-x-auto pb-2">
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isComplete = currentStep > step.id;
            
            return (
              <button
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                className={`flex flex-col items-center min-w-[60px] transition-all ${
                  isActive ? 'text-primary' : isComplete ? 'text-green-500' : 'text-gray-500'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                  isActive ? 'bg-primary text-white' : isComplete ? 'bg-green-500 text-white' : 'bg-secondary'
                }`}>
                  {isComplete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className="text-xs hidden md:block">{step.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Form Content */}
      <div className="card-tactical">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        
        {currentStep < STEPS.length ? (
          <Button
            onClick={() => setCurrentStep(Math.min(STEPS.length, currentStep + 1))}
            className="bg-primary hover:bg-red-700"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-green-600 hover:bg-green-700"
            data-testid="intake-submit-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Submit Intake Form
          </Button>
        )}
      </div>
    </div>
  );
}
